import { useMemo, useState } from 'react';
import { getPasswordRequirementState } from '../../../utils/passwordValidation';

function RequirementLine({ met, label, active }) {
  const color = met ? '#166534' : active ? '#991b1b' : '#6b7280';
  return (
    <li style={{ color, marginBottom: '0.2rem' }}>
      {met ? '✓' : '•'} {label}
    </li>
  );
}

export default function PasswordInput({
  value,
  onChange,
  inputId = 'password',
  label = 'Password',
  name = 'password',
  autoComplete = 'new-password',
  disabled = false,
  required = true,
  placeholder = 'Password',
  showRequirements = false,
  confirmValue = '',
  onConfirmChange,
  confirmInputId = 'confirm-password',
  confirmLabel = 'Confirm Password',
  confirmName = 'confirmPassword',
  confirmAutoComplete = 'new-password',
  confirmPlaceholder = 'Confirm Password',
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const requirementState = useMemo(() => getPasswordRequirementState(value), [value]);
  const hasPassword = String(value || '').length > 0;
  const hasConfirmation = String(confirmValue || '').length > 0;
  const shouldShowConfirm = typeof onConfirmChange === 'function';
  const passwordsMatch = !shouldShowConfirm || (hasConfirmation && value === confirmValue);

  const renderPasswordField = ({
    id,
    fieldLabel,
    fieldName,
    fieldValue,
    fieldOnChange,
    fieldAutoComplete,
    fieldPlaceholder,
    visible,
    onToggle,
  }) => (
    <div className="flex-column" style={{ gap: '0.35rem' }}>
      <label htmlFor={id} style={{ fontWeight: 600 }}>{fieldLabel}</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center' }}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          name={fieldName}
          autoComplete={fieldAutoComplete}
          autoCapitalize="none"
          spellCheck={false}
          placeholder={fieldPlaceholder}
          value={fieldValue}
          onChange={fieldOnChange}
          required={required}
          disabled={disabled}
          className="form-input"
        />
        <button
          type="button"
          className="btn btn--secondary btn--xs"
          onClick={onToggle}
          aria-label={`${visible ? 'Hide' : 'Show'} ${fieldLabel.toLowerCase()}`}
          aria-pressed={visible}
          disabled={disabled}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex-column" style={{ gap: '0.75rem' }}>
      {renderPasswordField({
        id: inputId,
        fieldLabel: label,
        fieldName: name,
        fieldValue: value,
        fieldOnChange: onChange,
        fieldAutoComplete: autoComplete,
        fieldPlaceholder: placeholder,
        visible: showPassword,
        onToggle: () => setShowPassword((current) => !current),
      })}

      {shouldShowConfirm && (
        <div className="flex-column" style={{ gap: '0.35rem' }}>
          {renderPasswordField({
            id: confirmInputId,
            fieldLabel: confirmLabel,
            fieldName: confirmName,
            fieldValue: confirmValue,
            fieldOnChange: onConfirmChange,
            fieldAutoComplete: confirmAutoComplete,
            fieldPlaceholder: confirmPlaceholder,
            visible: showConfirmPassword,
            onToggle: () => setShowConfirmPassword((current) => !current),
          })}
          {hasConfirmation && (
            <p style={{ color: passwordsMatch ? '#166534' : '#991b1b', margin: 0, fontSize: '0.9rem' }}>
              {passwordsMatch ? 'Passwords match.' : 'Passwords do not match.'}
            </p>
          )}
        </div>
      )}

      {showRequirements && (
        <div>
          <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Password requirements</p>
          <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1rem', fontSize: '0.9rem' }}>
            {requirementState.map((requirement) => (
              <RequirementLine
                key={requirement.key}
                label={requirement.label}
                met={requirement.met}
                active={hasPassword}
              />
            ))}
            {shouldShowConfirm && (
              <RequirementLine
                label="Passwords match"
                met={passwordsMatch}
                active={hasConfirmation}
              />
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
